import React from 'react';
import {
    COLOR_01,
    COLOR_10,
    COUNTRY_COMPARISON_RESULTS_MAX_SELECTION,
    MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_DATE,
    MESSAGE_NOT_AVAILABLE_PROFILE,
    NOT_APPLICABLE_TEXT
} from '../../../constants';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { ComponentWithFilterHandlersProps, CountryProfile, Taxonomies } from '../../../types';
import { checkSpecialConditionToHideTable, getLatestDates, getTaxonomyName, interpolateString } from '../../../utils';
import DynamicNumber, { N } from '../../layout/dynamic-number/dynamic-number.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import InfoDate from '../info-date/info-date.component';
import LegalProvisions from '../legal-provisions/legal-provisions.component';
import PageSection from '../../layout/page-section/page-section.component';
import ProgressBar from '../../layout/progress-bar/progress-bar.component';
import ResultsButtonpad from '../results-buttonpad/results-buttonpad.component';
import Table from '../../layout/table/table.component';
import {
    countryComparisonResultsTableHeadCellClassName,
    countryComparisonResultsCountryNameClassName,
    countryComparisonResultsCountryNameCaptionClassName,
    countryComparisonResultsListClassName,
    countryComparisonResultsQuotaValueCellClassName
} from './country-comparison-results.style';
import Comments from '../../layout/comments/comments.component';
import { debugRun } from '../../../debug/debug.utils';
import Text from '../../layout/text/text.component';
import Box from '../../layout/box/box.component';
import Spacer from '../../layout/spacer/spacer.component';

interface CountryComparisonResultsProps extends ComponentWithFilterHandlersProps {
    countryProfileList: CountryProfile[],
    invalidCountryIds: number[],
}

const CountryComparisonResults: React.FC<CountryComparisonResultsProps> = ({
    countryProfileList,
    invalidCountryIds,
    filters,
    onChangeFilters
}) => {
    const theme = filters.localLevel ? 'green' : 'blue';
    const [{taxonomies}] = useAppState();
    const selectedResults = countryProfileList.slice(0, COUNTRY_COMPARISON_RESULTS_MAX_SELECTION);
    const selectedCountryIds = filters.countryIds.length ? filters.countryIds : selectedResults.map(countryProfile => countryProfile.countryId);
    const selectedCountryResults = selectedCountryIds.map(countryId => {
        return {
            countryId,
            countryName: getTaxonomyName(taxonomies, 'countries', countryId),
            countryProfile: (() => {
                for (const countryProfile of selectedResults) {
                    if (countryProfile.countryId === countryId) {
                        return countryProfile;
                    }
                }

                return null;
            })(),
        };
    });
    const columnWidthPercentage: number = 100 / Math.min(selectedCountryIds.length, COUNTRY_COMPARISON_RESULTS_MAX_SELECTION);

    const createRowForUI = (
        headerName: string | React.ReactNode,
        keyNameOrContentForIteration: (keyof CountryProfile) | ((countryProfile: CountryProfile, countryName: string) => React.ReactNode),
        taxonomyName?: keyof Taxonomies,
        fallbackContent?: (result: typeof selectedCountryResults[number]) => React.ReactNode
    ): React.ReactNode => (
        <tr>
            <th className={ countryComparisonResultsTableHeadCellClassName }>
                { headerName }
            </th>
            { selectedCountryResults.map((result, index) => {
                if (!result.countryProfile) {
                    return (
                        <td key={ index } style={ {width: `${columnWidthPercentage}%`} }>
                            { fallbackContent && fallbackContent(result) }
                        </td>
                    )
                }

                if (typeof keyNameOrContentForIteration === 'string') {
                    if (taxonomyName) {
                        return (
                            <td key={ index } style={ {width: `${columnWidthPercentage}%`} }>
                                { getTaxonomyName(
                                    taxonomies,
                                    taxonomyName,
                                    result.countryProfile[keyNameOrContentForIteration] as number
                                ) }
                            </td>
                        );
                    }
                    else {
                        return (
                            <td key={ index }>
                                { String(result.countryProfile[keyNameOrContentForIteration]) }
                            </td>
                        );
                    }
                }
                else {
                    return (
                        <td key={ index }>
                            { keyNameOrContentForIteration(result.countryProfile, result.countryName) }
                        </td>
                    );
                }
            }) }
        </tr>
    );

    const createRowForDownload = (
        headerName: string,
        keyNameOrContentForIteration: (keyof CountryProfile) | ((result: typeof selectedCountryResults[number]) => string),
        taxonomyName?: keyof Taxonomies,
    ): string[] => ([
        headerName,
        ...selectedCountryResults.map(result => {
            if (typeof keyNameOrContentForIteration === 'function') {
                return keyNameOrContentForIteration(result);
            }
            else if (result.countryProfile) {
                if (taxonomyName) {
                    if (Array.isArray(result.countryProfile[keyNameOrContentForIteration])) {
                        return (result.countryProfile[keyNameOrContentForIteration] as number[])
                            .map((taxonomyId: number) => getTaxonomyName(taxonomies, taxonomyName, taxonomyId))
                            .join(', ');
                    }

                    return getTaxonomyName(taxonomies, taxonomyName, result.countryProfile[keyNameOrContentForIteration] as number);
                }

                return String(result.countryProfile[keyNameOrContentForIteration]);
            }

            return '';
        }),
    ]);

    const noCountryProfileMessage = (
        countryId: number
    ) => interpolateString(MESSAGE_NOT_AVAILABLE_PROFILE, {
        for: invalidCountryIds.includes(countryId) ? 'this country' : 'selected year'
    });

    const downloadDataHeaders = ['Country', ...selectedCountryResults.map(result => result.countryName)];
    const resultsButtonpad = (
        <ResultsButtonpad
            pdf={ {columnSizePercentages: [10, ...downloadDataHeaders.map(() => 90 / (downloadDataHeaders.length - 1))]} }
            downloadData={ {
                fileName: 'country-comparison-' + (filters.localLevel ? 'local-government' : 'parliament'),
                tables: [
                    {
                        title: 'Country comparison',
                        subtitle: filters.localLevel ? 'Local government' : 'Parliament',
                        headers: downloadDataHeaders,
                        rows: (() => {
                            const messageRow = createRowForDownload('', result => result.countryProfile ? '' : noCountryProfileMessage(result.countryId));
                            const rows = [
                                !filters.localLevel && createRowForDownload('Parliament structure', 'parliamentStructureId', 'parliamentStructure'),
                                createRowForDownload('Electoral system', 'electoralSystemTypeId', 'electoralSystemTypes'),
                                createRowForDownload('Electoral system (sub-type)', 'electoralSystemSubType'),
                                createRowForDownload('Type of legislated quota', 'typeOfQuotaIds', 'typesOfQuota'),
                                filters.localLevel && createRowForDownload('Quota Application', 'quotaApplication'),
                                createRowForDownload('Quota target', (result) => result.countryProfile ? result.countryProfile.quotaTarget + '%' : ''),
                                createRowForDownload('Women\'s representation', (result) => (
                                    result.countryProfile
                                        ? result.countryProfile.womensRepresentation === null
                                            ? NOT_APPLICABLE_TEXT
                                            : result.countryProfile.womensRepresentation + '%'
                                        : ''
                                )),
                                !filters.localLevel && createRowForDownload('Quota met', 'quotaTargetMetId', 'quotaTargetMet'),
                                createRowForDownload('Provision: Constitution', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.constitutionLegalSource}. ${result.countryProfile.constitutionLegalProvisions}`
                                        : ''
                                )),
                                createRowForDownload('Provision: Electoral law', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.electoralLegalSource}. ${result.countryProfile.electoralLegalProvisions}`
                                        : ''
                                )),
                                filters.localLevel && createRowForDownload('Provision: Local government law', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.localLegalSource}. ${result.countryProfile.localLegalProvisions}`
                                        : ''
                                )),
                                createRowForDownload('Provision: Party law', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.partyLegalSource}. ${result.countryProfile.partyLegalProvisions}`
                                        : ''
                                )),
                                createRowForDownload('Provision: Parity law', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.parityLegalSource}. ${result.countryProfile.parityLegalProvisions}`
                                        : ''
                                )),
                                createRowForDownload('Provision: Financing law', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.financingLegalSource}. ${result.countryProfile.financingLegalProvisions}`
                                        : ''
                                )),
                                createRowForDownload('Provision: Political agreement', (result) => (
                                    result.countryProfile
                                        ? `${result.countryProfile.politicalLegalSource}. ${result.countryProfile.politicalLegalProvisions}`
                                        : ''
                                )),
                            ];

                            if (messageRow.some(messageCell => messageCell !== '')) {
                                rows.unshift(messageRow);
                            }

                            return rows;
                        })(),
                    }
                ]
            } }
            datetime={ filters.datetime }
            onChangeDate={ datetime => onChangeFilters({
                ...filters, datetime
            }) }
            localLevel={ filters.localLevel }
            // onChangeLocalLevel={ localLevel => onChangeFilters({
            //         ...filters, localLevel,
            // }) }
            />
    );

    const specialConditionToHideTable = checkSpecialConditionToHideTable(
        filters,
        selectedCountryResults.length
    );

    debugRun(({ log }) => {
        specialConditionToHideTable && log('Hiding table due to special condition being met');
    });

    return (
        <PageSection title='Country comparison results' aside={ resultsButtonpad }>
            <div id='print'>
                { specialConditionToHideTable
                ? (
                    <Box>
                        <Spacer>
                            <Text>{ MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_DATE }</Text>
                        </Spacer>
                    </Box>
                )
                : (
                    <Table theme={ theme }>
                        <tbody>
                            { createRowForUI('Country', (countryProfile, countryName) => (
                                <span className={ countryComparisonResultsCountryNameClassName }>
                                    { countryName }
                                </span>
                            ), undefined, result => (
                                <>
                                    <span className={ countryComparisonResultsCountryNameClassName }>
                                        { result.countryName }
                                    </span>
                                    <span className={ countryComparisonResultsCountryNameCaptionClassName }>
                                        { noCountryProfileMessage(result.countryId) }
                                    </span>
                                </>
                            )) }
                            { !filters.localLevel && createRowForUI('Parliament structure', 'parliamentStructureId', 'parliamentStructure') }
                            { createRowForUI('Electoral system', 'electoralSystemTypeId', 'electoralSystemTypes') }
                            { createRowForUI('Electoral system (sub-type)', 'electoralSystemSubType') }
                            { createRowForUI('Type of legislated quota', result => (
                                result.typeOfQuotaIds.map((typeOfQuotaId, index) => (
                                    <div key={ index }>
                                        { getTaxonomyName(taxonomies, 'typesOfQuota', typeOfQuotaId) }
                                    </div>
                                ))
                            ) ) }
                            { filters.localLevel && createRowForUI('Quota Application', 'quotaApplication') }
                            { createRowForUI((
                                <ul className={ countryComparisonResultsListClassName }>
                                    <li><div>Quota target</div></li>
                                    <li><div>Women's representation</div></li>
                                </ul>
                            ), result => (
                                <>
                                    <Grid>
                                        <Cell size={ 6 }>
                                            <ProgressBar progress={ result.quotaTarget } color={ COLOR_10 }/>
                                        </Cell>
                                        <Cell size={ 6 } className={ countryComparisonResultsQuotaValueCellClassName }>
                                            <N>{ result.quotaTarget }</N>%
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell size={ 6 }>
                                            { (result.womensRepresentation !== null) && (
                                                <ProgressBar progress={ result.womensRepresentation || 0 } color={ COLOR_01 }/>
                                            ) }
                                        </Cell>
                                        <Cell size={ 6 } className={ countryComparisonResultsQuotaValueCellClassName }>
                                            <span>
                                                {
                                                    result.womensRepresentation === null
                                                    ? NOT_APPLICABLE_TEXT
                                                    : <><DynamicNumber value={ result.womensRepresentation }/>%</>
                                                }
                                            </span>
                                        </Cell>
                                    </Grid>
                                </>
                            )) }
                            { !filters.localLevel && createRowForUI('Quota met', 'quotaTargetMetId', 'quotaTargetMet') }
                            { createRowForUI((
                                <>
                                    Provisions
                                    <Comments>
                                        Note: Please click on the provisions to view and compare
                                    </Comments>
                                </>
                            ), result => (
                                <LegalProvisions countryProfile={ result } showAll/>
                            )) }
                        </tbody>
                    </Table>
                ) }
                <InfoDate dates={ getLatestDates(selectedResults) } localLevel={ filters.localLevel }/>
            </div>
        </PageSection>
    );
};

export default CountryComparisonResults;
